'use client';
import { Layout, LayoutItem } from '@mnd-frontend/ui/publish/components';
import { memo, useMemo } from 'react';
import { ContextProvider } from '../lib/context';
import { useColorAndId } from '../lib/hooks/data';
import { compareComponentsProps } from '../lib/utils';
import BubbleChart from './BubbleChart';
import DataTableContainer from './DataTableContainer';
import TimeAreaChart from './TimeAreaChart';

const useSortByDate = (data: any, dateKey: any) =>
  useMemo(
    () =>
      data
        .map((d: any) => ({ ...d, date: new Date(d[dateKey]) }))
        .sort((a: any, b: any) => a.date - b.date),
    [data, dateKey],
  );

const BubbleTimeline = ({ data, mappings, dateKey, markers, utcOffset }: any) => {
  const mapping = mappings[0];
  const sorted = useSortByDate(data, dateKey);
  const hasValue = data.length > 0 && Boolean(sorted.find((d: any) => d[mapping.dataKey] > 0));
  return (
    <Layout gap={2} $alignItems="center">
      <LayoutItem $basis="1/2" $grow>
        <DataTableContainer data={sorted} utcOffset={utcOffset} />
      </LayoutItem>
      {hasValue && (
        <LayoutItem $basis="1/2" $grow style={{ position: 'relative' }}>
          <BubbleChart
            data={sorted}
            dataKey={mapping.dataKey}
            color={mapping.color}
            minWidth={240}
            height={320}
            utcOffset={utcOffset}
          />
        </LayoutItem>
      )}
      <LayoutItem $minWidth="100%" style={{ position: 'relative' }}>
        <TimeAreaChart
          data={sorted}
          markers={markers}
          utcOffset={utcOffset}
          dataKey={mapping.dataKey}
          color={mapping.color}
        />
      </LayoutItem>
    </Layout>
  );
};

const BubbleTimelineProvider = memo(({ data, mappings, components, ...props }: any) => {
  const coloredMappings = useColorAndId(mappings);
  return (
    <ContextProvider minWidth={200} height={320} components={components}>
      <BubbleTimeline data={data} mappings={coloredMappings} {...props} />
    </ContextProvider>
  );
}, compareComponentsProps as any);

(BubbleTimelineProvider as any).defaultProps = {
  utcOffset: 0,
  dateKey: 'date',
};

export default BubbleTimelineProvider;
